/**
 * ProjectListScreen.js
 * Central hub that integrates all project-related screens:
 * ProjectDetailsScreen, ProjectAnalyticsScreen, ProjectTeamScreen,
 * ProjectMilestonesScreen, ProjectRisksScreen, ProjectScheduleScreen,
 * ProjectPlanningScreen, ProjectManagementScreen, ProjectAssignment,
 * ProjectProductCreationScreen, ProjectsScreen
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView,
  TextInput, FlatList, RefreshControl, Modal, Alert,
  Image, Dimensions, Platform, ActivityIndicator, StatusBar,
  Animated,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');

// ─── API Config (matches all child screens) ──────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getHeaders = async () => {
  const tok = await AsyncStorage.getItem('auth_token').catch(() => null)
            || await AsyncStorage.getItem('token').catch(() => null);
  const cid = await AsyncStorage.getItem('company_id').catch(() => null);
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    'X-API-Key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

// ─── Design System ────────────────────────────────────────────────────────────
const C = {
  primary:   '#0e4e96',
  secondary: '#1e7be2',
  bg:        '#F0F4F8',
  card:      '#FFFFFF',
  border:    '#E2E8F0',
  text:      '#1E293B',
  textSec:   '#64748B',
  success:   '#10B981',
  warning:   '#F59E0B',
  danger:    '#EF4444',
  info:      '#3B82F6',
  purple:    '#8B5CF6',
  cyan:      '#06B6D4',
};

const STATUS_MAP = {
  'On Track':    { color: C.success,  bg: '#ECFDF5', icon: 'check-circle' },
  'on-track':    { color: C.success,  bg: '#ECFDF5', icon: 'check-circle' },
  'in-progress': { color: C.info,     bg: '#EFF6FF', icon: 'progress-clock' },
  'In Progress': { color: C.info,     bg: '#EFF6FF', icon: 'progress-clock' },
  'Delayed':     { color: C.danger,   bg: '#FEF2F2', icon: 'alert-circle' },
  'delayed':     { color: C.danger,   bg: '#FEF2F2', icon: 'alert-circle' },
  'Completed':   { color: C.textSec,  bg: '#F1F5F9', icon: 'check-all' },
  'completed':   { color: C.textSec,  bg: '#F1F5F9', icon: 'check-all' },
  'Planning':    { color: C.warning,  bg: '#FFFBEB', icon: 'clipboard-text' },
  'planning':    { color: C.warning,  bg: '#FFFBEB', icon: 'clipboard-text' },
  'on-hold':     { color: C.purple,   bg: '#F5F3FF', icon: 'pause-circle' },
  'On Hold':     { color: C.purple,   bg: '#F5F3FF', icon: 'pause-circle' },
};

const PRIORITY_MAP = {
  high:   { color: C.danger,  bg: '#FEF2F2' },
  High:   { color: C.danger,  bg: '#FEF2F2' },
  medium: { color: C.warning, bg: '#FFFBEB' },
  Medium: { color: C.warning, bg: '#FFFBEB' },
  low:    { color: C.success, bg: '#ECFDF5' },
  Low:    { color: C.success, bg: '#ECFDF5' },
};

const fmt = (v) => `£${parseFloat(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// ─── Normalize project from any source schema ─────────────────────────────────
const normalizeProject = (raw) => ({
  id:           raw.id           || raw.projectId      || String(Math.random()),
  name:         raw.name         || raw.projectName    || raw.title       || 'Untitled Project',
  description:  raw.description  || raw.lastUpdate     || '',
  client:       raw.client       || raw.client_name    || raw.client_company || 'No Client',
  status:       raw.status       || 'Planning',
  priority:     raw.priority     || 'Medium',
  progress:     parseInt(raw.progress || raw.overallProgress || 0),
  budget:       parseFloat(raw.budget || 0),
  spent:        parseFloat(raw.spent  || 0),
  currency:     raw.currency     || 'GBP',
  startDate:    raw.startDate    || raw.start_date     || null,
  endDate:      raw.endDate      || raw.end_date       || raw.endDate     || null,
  projectManager: raw.projectManager || raw.project_manager || '',
  category:     raw.category     || '',
  teamSize:     raw.teamSize     || raw.team?.totalMembers || raw.team_size || 0,
  milestones:   raw.milestones   || [],
  risks:        raw.risks        || [],
  teamMembers:  raw.teamMembers  || raw.team?.members  || [],
  tags:         raw.tags         || [],
  location:     raw.location     || '',
  currentPhase: raw.currentPhase || '',
  involvedPartners: raw.involvedPartners || [],
  _raw:         raw,
});

// ─── Quick action buttons per project ────────────────────────────────────────
const PROJECT_QUICK_ACTIONS = [
  { key: 'details',    icon: 'information',           label: 'Details',    color: C.primary,  screen: 'ProjectDetails' },
  { key: 'milestones', icon: 'flag-checkered',         label: 'Milestones', color: C.info,     screen: 'ProjectMilestones' },
  { key: 'team',       icon: 'account-group',          label: 'Team',       color: C.cyan,     screen: 'ProjectTeam' },
  { key: 'risks',      icon: 'alert-circle-outline',   label: 'Risks',      color: C.danger,   screen: 'ProjectRisks' },
  { key: 'analytics',  icon: 'chart-bar',              label: 'Analytics',  color: C.purple,   screen: 'ProjectAnalytics' },
  { key: 'schedule',   icon: 'calendar-clock',         label: 'Schedule',   color: C.warning,  screen: 'ProjectSchedule' },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ProjectListScreen({ navigation, route }) {
  const [projects, setProjects]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [refreshing, setRefreshing]       = useState(false);
  const [searchQuery, setSearchQuery]     = useState('');
  const [activeFilter, setActiveFilter]   = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [sortBy, setSortBy]               = useState('name');
  const [viewMode, setViewMode]           = useState('list');
  const [companyLogo, setCompanyLogo]     = useState(null);
  const [companyId, setCompanyId]         = useState(null);
  const [showFilters, setShowFilters]     = useState(false);
  const [showNewModal, setShowNewModal]   = useState(false);
  const [creating, setCreating]           = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [fadeAnim] = useState(new Animated.Value(0));

  const [newProject, setNewProject] = useState({
    name: '', description: '', client: '', status: 'Planning',
    priority: 'Medium', budget: '', startDate: '', endDate: '',
    projectManager: '', category: '', location: '',
  });

  const FILTERS = ['All', 'On Track', 'In Progress', 'Planning', 'Delayed', 'Completed', 'On Hold'];
  const PRIORITIES = ['All', 'High', 'Medium', 'Low'];
  const SORT_OPTIONS = [
    { key: 'name', label: 'Name' }, { key: 'status', label: 'Status' },
    { key: 'progress', label: 'Progress' }, { key: 'endDate', label: 'Due Date' },
    { key: 'budget', label: 'Budget' },
  ];

  useEffect(() => { init(); }, []);

  const init = async () => {
    const cid = await AsyncStorage.getItem('company_id').catch(() => null);
    setCompanyId(cid);
    await Promise.all([loadProjects(cid), loadCompanyLogo(cid)]);
    Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
  };

  const loadCompanyLogo = async (cid) => {
    try {
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const p = JSON.parse(cached);
        const logo = p.logo || p.logo_url || p.company_logo;
        if (logo) setCompanyLogo(logo.startsWith('http') || logo.startsWith('data:') ? logo : `${API_BASE}${logo}`);
      }
    } catch {}
  };

  const loadProjects = async (cid) => {
    setLoading(true);
    try {
      const h = await getHeaders();
      const id = cid || companyId;

      // Try multiple endpoints — all child screens use these
      const [r1, r2] = await Promise.allSettled([
        fetch(`${API_BASE}/api/projects${id ? `?company_id=${parseInt(id)||0}` : ''}`, { headers: h }),
        fetch(`${API_BASE}/api/projects${id ? `?company_id=${parseInt(id)||0}` : ''}`, { headers: h }),
      ]);

      let raw = [];
      for (const r of [r1, r2]) {
        if (r.status === 'fulfilled' && r.value.ok) {
          const json = await r.value.json().catch(() => ({}));
          const data = Array.isArray(json) ? json : (json?.data ?? json?.projects ?? []);
          if (Array.isArray(data) && data.length > 0) { raw = data; break; }
        }
      }

      // Try AsyncStorage caches written by child screens
      if (!raw.length) {
        const sources = ['company_projects', 'projects_cache', 'project_list'];
        for (const key of sources) {
          const cached = await AsyncStorage.getItem(key).catch(() => null);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed) && parsed.length) { raw = parsed; break; }
          }
        }
      }

      const normalized = raw.map(normalizeProject);
      setProjects(normalized);
      await AsyncStorage.setItem('projects_cache', JSON.stringify(raw));
    } catch (e) {
      console.warn('[ProjectList] Load failed:', e.message);
      // Load from cache
      const cached = await AsyncStorage.getItem('projects_cache').catch(() => null)
                  || await AsyncStorage.getItem('company_projects').catch(() => null);
      if (cached) setProjects(JSON.parse(cached).map(normalizeProject));
    } finally { setLoading(false); setRefreshing(false); }
  };

  const createProject = async () => {
    if (!newProject.name.trim()) { Alert.alert('Required', 'Please enter a project name'); return; }
    setCreating(true);
    try {
      const h = await getHeaders();
      const payload = {
        ...newProject,
        company_id:  parseInt(companyId) || 0,
        company_id: parseInt(companyId) || 0,
        budget:      parseFloat(newProject.budget) || 0,
      };
      const res = await fetch(`${API_BASE}/api/projects`, { method: 'POST', headers: h, body: JSON.stringify(payload) });
      const json = await res.json().catch(() => ({}));
      const created = json?.data || { ...payload, id: `local_${Date.now()}` };
      const norm = normalizeProject(created);
      setProjects(prev => [norm, ...prev]);
      const cache = [created, ...(JSON.parse(await AsyncStorage.getItem('projects_cache').catch(() => '[]') || '[]'))];
      await AsyncStorage.setItem('projects_cache', JSON.stringify(cache));
      await AsyncStorage.setItem('company_projects', JSON.stringify(cache));
      setShowNewModal(false);
      setNewProject({ name:'', description:'', client:'', status:'Planning', priority:'Medium', budget:'', startDate:'', endDate:'', projectManager:'', category:'', location:'' });
      Alert.alert('✅ Project Created', `"${norm.name}" has been added`);
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setCreating(false); }
  };

  const deleteProject = (project) => {
    Alert.alert('Delete Project', `Delete "${project.name}"? This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try {
          const h = await getHeaders();
          fetch(`${API_BASE}/api/projects/${project.id}`, { method: 'DELETE', headers: h }).catch(() => {});
          setProjects(prev => prev.filter(p => p.id !== project.id));
          const cache = JSON.parse(await AsyncStorage.getItem('projects_cache').catch(() => '[]') || '[]')
            .filter(p => p.id !== project.id);
          await AsyncStorage.setItem('projects_cache', JSON.stringify(cache));
          await AsyncStorage.setItem('company_projects', JSON.stringify(cache));
        } catch (e) { Alert.alert('Error', e.message); }
        setShowActionSheet(false);
      }},
    ]);
  };

  const navigateTo = (screen, project) => {
    const raw = project._raw || project;
    const params = {
      project: { ...raw, ...project },
      projectId: project.id,
    };
    // Map screen names to correct param shapes each child screen expects
    switch (screen) {
      case 'ProjectDetails':    navigation.navigate('ProjectDetails',    { projectId: project.id }); break;
      case 'ProjectAnalytics':  navigation.navigate('ProjectAnalytics',  { project: raw }); break;
      case 'ProjectTeam':       navigation.navigate('ProjectTeam',       { project: raw }); break;
      case 'ProjectMilestones': navigation.navigate('ProjectMilestones', { project: raw }); break;
      case 'ProjectRisks':      navigation.navigate('ProjectRisks',      { project: raw }); break;
      case 'ProjectSchedule':   navigation.navigate('ProjectSchedule',   { project: raw }); break;
      case 'ProjectPlanning':   navigation.navigate('ProjectPlanning',   { project: raw }); break;
      case 'ProjectManagement': navigation.navigate('ProjectManagement', { project: raw }); break;
      case 'ProjectAssignment': navigation.navigate('ProjectAssignment', { project: raw }); break;
      default: navigation.navigate(screen, params);
    }
    setShowActionSheet(false);
  };

  const filtered = useMemo(() => {
    let list = [...projects];
    if (activeFilter !== 'All') {
      list = list.filter(p => {
        const s = (p.status || '').toLowerCase();
        const f = activeFilter.toLowerCase().replace(' ', '-');
        return s === f || s === activeFilter.toLowerCase();
      });
    }
    if (priorityFilter !== 'All') list = list.filter(p => (p.priority||'').toLowerCase() === priorityFilter.toLowerCase());
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p => String(p.name ?? '').toLowerCase().includes(q) || String(p.client ?? '').toLowerCase().includes(q) || String(p.category ?? '').toLowerCase().includes(q) || String(p.description ?? '').toLowerCase().includes(q));
    }
    switch (sortBy) {
      case 'progress': list.sort((a, b) => b.progress - a.progress); break;
      case 'endDate':  list.sort((a, b) => new Date(a.endDate||0) - new Date(b.endDate||0)); break;
      case 'budget':   list.sort((a, b) => b.budget - a.budget); break;
      case 'status':   list.sort((a, b) => (a.status||'').localeCompare(b.status||'')); break;
      default:         list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  }, [projects, activeFilter, priorityFilter, searchQuery, sortBy]);

  const stats = useMemo(() => ({
    total:     projects.length,
    active:    projects.filter(p => ['On Track','In Progress','in-progress','on-track'].includes(p.status)).length,
    delayed:   projects.filter(p => ['Delayed','delayed'].includes(p.status)).length,
    completed: projects.filter(p => ['Completed','completed'].includes(p.status)).length,
    totalBudget: projects.reduce((s, p) => s + p.budget, 0),
    totalSpent:  projects.reduce((s, p) => s + p.spent, 0),
    avgProgress: projects.length ? Math.round(projects.reduce((s, p) => s + p.progress, 0) / projects.length) : 0,
  }), [projects]);

  // ─── Render helpers ───────────────────────────────────────────────────────
  const StatusBadge = ({ status, small }) => {
    const info = STATUS_MAP[status] || { color: C.textSec, bg: '#F1F5F9', icon: 'circle' };
    return (
      <View style={[s.badge, { backgroundColor: info.bg }]}>
        <MaterialCommunityIcons name={info.icon} size={small ? 10 : 12} color={info.color} />
        <Text style={[s.badgeText, { color: info.color, fontSize: small ? 9 : 10 }]}>{status}</Text>
      </View>
    );
  };

  const PriorityDot = ({ priority }) => {
    const info = PRIORITY_MAP[priority] || { color: C.textSec };
    return <View style={[s.priorityDot, { backgroundColor: info.color }]} />;
  };

  const renderProjectCard = ({ item: p, index }) => {
    const statusInfo = STATUS_MAP[p.status] || { color: C.textSec, bg: '#F1F5F9', icon: 'circle' };
    const budgetPct = p.budget > 0 ? Math.round((p.spent / p.budget) * 100) : 0;
    const isOverBudget = budgetPct > 100;
    const isOverdue = p.endDate && new Date(p.endDate) < new Date() && p.status !== 'Completed' && p.status !== 'completed';

    if (viewMode === 'grid') return renderGridCard({ item: p });

    return (
      <Animated.View style={[s.card, { opacity: fadeAnim, transform: [{ translateY: fadeAnim.interpolate({ inputRange:[0,1], outputRange:[20,0] }) }] }]}>
        <TouchableOpacity
          style={s.cardInner}
          onPress={() => navigateTo('ProjectDetails', p)}
          onLongPress={() => { setSelectedProject(p); setShowActionSheet(true); }}
          activeOpacity={0.85}
        >
          {/* Card Header */}
          <View style={s.cardHeader}>
            <View style={{ flex: 1 }}>
              <View style={s.cardTitleRow}>
                <PriorityDot priority={p.priority} />
                <Text style={s.cardName} numberOfLines={1}>{p.name}</Text>
              </View>
              <Text style={s.cardClient} numberOfLines={1}>{p.client}</Text>
            </View>
            <View style={{ alignItems: 'flex-end', gap: 4 }}>
              <StatusBadge status={p.status} />
              {isOverdue && <View style={[s.badge, { backgroundColor: '#FEF2F2' }]}><Text style={[s.badgeText, { color: C.danger }]}>OVERDUE</Text></View>}
            </View>
          </View>

          {/* Progress Bar */}
          <View style={s.progressSection}>
            <View style={s.progressLabelRow}>
              <Text style={s.progressLabel}>{p.currentPhase || p.category}</Text>
              <Text style={[s.progressPct, { color: statusInfo.color }]}>{p.progress}%</Text>
            </View>
            <View style={s.progressTrack}>
              <View style={[s.progressFill, { width: `${Math.min(100, p.progress)}%`, backgroundColor: statusInfo.color }]} />
            </View>
          </View>

          {/* Metrics Row */}
          <View style={s.metricsRow}>
            <View style={s.metric}>
              <MaterialCommunityIcons name="account-group-outline" size={13} color={C.textSec} />
              <Text style={s.metricText}>{p.teamSize || p.teamMembers?.length || 0} members</Text>
            </View>
            <View style={s.metric}>
              <MaterialCommunityIcons name="calendar-end" size={13} color={isOverdue ? C.danger : C.textSec} />
              <Text style={[s.metricText, isOverdue && { color: C.danger }]}>{fmtDate(p.endDate)}</Text>
            </View>
            <View style={s.metric}>
              <MaterialCommunityIcons name="currency-gbp" size={13} color={isOverBudget ? C.danger : C.success} />
              <Text style={[s.metricText, isOverBudget && { color: C.danger }]}>{fmt(p.spent)} / {fmt(p.budget)}</Text>
            </View>
          </View>

          {/* Milestones mini-bar */}
          {p.milestones?.length > 0 && (
            <View style={s.milestonesRow}>
              {p.milestones.slice(0, 6).map((m, i) => {
                const mc = m.status === 'Completed' || m.status === 'completed' ? C.success : m.status === 'In Progress' ? C.info : m.status === 'Delayed' ? C.danger : C.border;
                return <View key={i} style={[s.milestoneDot, { backgroundColor: mc }]} />;
              })}
              {p.milestones.length > 6 && <Text style={s.milestonesMore}>+{p.milestones.length - 6}</Text>}
              <Text style={s.milestonesLabel}>{p.milestones.filter(m => ['Completed','completed'].includes(m.status)).length}/{p.milestones.length} milestones</Text>
            </View>
          )}

          {/* Quick Actions */}
          <View style={s.quickActions}>
            {PROJECT_QUICK_ACTIONS.map(action => (
              <TouchableOpacity
                key={action.key}
                style={s.quickAction}
                onPress={() => navigateTo(action.screen, p)}
              >
                <MaterialCommunityIcons name={action.icon} size={16} color={action.color} />
                <Text style={[s.quickActionText, { color: action.color }]}>{action.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Animated.View>
    );
  };

  const renderGridCard = ({ item: p }) => {
    const statusInfo = STATUS_MAP[p.status] || { color: C.textSec, bg: '#F1F5F9', icon: 'circle' };
    return (
      <TouchableOpacity style={s.gridCard} onPress={() => navigateTo('ProjectDetails', p)} onLongPress={() => { setSelectedProject(p); setShowActionSheet(true); }}>
        <View style={[s.gridColorBar, { backgroundColor: statusInfo.color }]} />
        <View style={s.gridContent}>
          <Text style={s.gridName} numberOfLines={2}>{p.name}</Text>
          <Text style={s.gridClient} numberOfLines={1}>{p.client}</Text>
          <View style={s.gridProgress}>
            <View style={s.progressTrack}>
              <View style={[s.progressFill, { width: `${Math.min(100, p.progress)}%`, backgroundColor: statusInfo.color }]} />
            </View>
            <Text style={[s.progressPct, { color: statusInfo.color, fontSize: 11 }]}>{p.progress}%</Text>
          </View>
          <View style={s.gridFooter}>
            <StatusBadge status={p.status} small />
            <Text style={s.gridDate}>{fmtDate(p.endDate)}</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderHeader = () => (
    <View>
      {/* Header */}
      <LinearGradient colors={[C.primary, C.secondary]} style={s.header}>
        <View style={s.headerTop}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={s.backBtn}>
            <Ionicons name="arrow-back" size={22} color="#fff" />
          </TouchableOpacity>
          <View style={s.headerCenter}>
            {companyLogo
              ? <Image source={{ uri: companyLogo || undefined }} style={s.headerLogo} resizeMode="contain" />
              : <MaterialCommunityIcons name="office-building" size={22} color="rgba(255,255,255,0.7)" />
            }
            <Text style={s.headerTitle}>Projects</Text>
          </View>
          <View style={s.headerActions}>
            <TouchableOpacity style={s.headerBtn} onPress={() => navigation.navigate('ProjectManagement')}>
              <MaterialCommunityIcons name="cog-outline" size={20} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={s.headerBtn} onPress={() => setShowNewModal(true)}>
              <MaterialCommunityIcons name="plus" size={22} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>

        {/* Stats Row */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.statsScroll}>
          {[
            { label: 'Total',     value: stats.total,       color: '#fff' },
            { label: 'Active',    value: stats.active,      color: '#34D399' },
            { label: 'Delayed',   value: stats.delayed,     color: '#FCA5A5' },
            { label: 'Done',      value: stats.completed,   color: '#93C5FD' },
            { label: 'Avg.',      value: `${stats.avgProgress}%`, color: '#FCD34D' },
          ].map(({ label, value, color }) => (
            <View key={label} style={s.statPill}>
              <Text style={[s.statPillVal, { color }]}>{value}</Text>
              <Text style={s.statPillLabel}>{label}</Text>
            </View>
          ))}
          <View style={s.statPill}>
            <Text style={[s.statPillVal, { color: '#6EE7B7', fontSize: 11 }]}>{fmt(stats.totalBudget)}</Text>
            <Text style={s.statPillLabel}>Budget</Text>
          </View>
        </ScrollView>
      </LinearGradient>

      {/* Search */}
      <View style={s.searchWrap}>
        <Ionicons name="search-outline" size={18} color={C.textSec} style={s.searchIcon} />
        <TextInput
          style={s.searchInput}
          placeholder="Search projects, clients, categories..."
          placeholderTextColor={C.textSec}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}><Ionicons name="close-circle" size={18} color={C.textSec} /></TouchableOpacity>
        )}
      </View>

      {/* Toolbar */}
      <View style={s.toolbar}>
        <TouchableOpacity style={s.toolbarBtn} onPress={() => setShowFilters(!showFilters)}>
          <MaterialCommunityIcons name="filter-variant" size={16} color={showFilters ? C.primary : C.textSec} />
          <Text style={[s.toolbarBtnText, showFilters && { color: C.primary }]}>Filter</Text>
        </TouchableOpacity>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.filterScroll} contentContainerStyle={{ gap: 6, paddingHorizontal: 4 }}>
          {FILTERS.map(f => (
            <TouchableOpacity key={f} style={[s.filterChip, activeFilter === f && s.filterChipActive]} onPress={() => setActiveFilter(f)}>
              <Text style={[s.filterChipText, activeFilter === f && s.filterChipTextActive]}>{f}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <View style={s.viewToggle}>
          <TouchableOpacity onPress={() => setViewMode('list')} style={[s.viewBtn, viewMode === 'list' && s.viewBtnActive]}>
            <MaterialCommunityIcons name="view-list" size={16} color={viewMode === 'list' ? C.primary : C.textSec} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setViewMode('grid')} style={[s.viewBtn, viewMode === 'grid' && s.viewBtnActive]}>
            <MaterialCommunityIcons name="view-grid" size={16} color={viewMode === 'grid' ? C.primary : C.textSec} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Extended Filters */}
      {showFilters && (
        <View style={s.extFilters}>
          <View style={s.extFilterRow}>
            <Text style={s.extFilterLabel}>Priority:</Text>
            {PRIORITIES.map(p => (
              <TouchableOpacity key={p} style={[s.extChip, priorityFilter === p && s.extChipActive]} onPress={() => setPriorityFilter(p)}>
                <Text style={[s.extChipText, priorityFilter === p && { color: C.primary }]}>{p}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={s.extFilterRow}>
            <Text style={s.extFilterLabel}>Sort by:</Text>
            {SORT_OPTIONS.map(opt => (
              <TouchableOpacity key={opt.key} style={[s.extChip, sortBy === opt.key && s.extChipActive]} onPress={() => setSortBy(opt.key)}>
                <Text style={[s.extChipText, sortBy === opt.key && { color: C.primary }]}>{opt.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {/* Hub navigation shortcuts */}
          <Text style={[s.extFilterLabel, { marginTop: 8, marginBottom: 6 }]}>Quick Navigate:</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
            {[
              { label: 'Planning',    icon: 'chart-gantt',        screen: 'ProjectPlanning' },
              { label: 'Management',  icon: 'view-dashboard',     screen: 'ProjectManagement' },
              { label: 'Products',    icon: 'package-variant',    screen: 'ProjectProductCreation' },
              { label: 'Assignment',  icon: 'account-arrow-right',screen: 'ProjectAssignment' },
              { label: 'All Projects',icon: 'folder-multiple',    screen: 'ProjectsScreen' },
            ].map(({ label, icon, screen }) => (
              <TouchableOpacity key={screen} style={s.navShortcut} onPress={() => { setShowFilters(false); navigation.navigate(screen); }}>
                <MaterialCommunityIcons name={icon} size={16} color={C.primary} />
                <Text style={s.navShortcutText}>{label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      <Text style={s.resultCount}>{filtered.length} project{filtered.length !== 1 ? 's' : ''}</Text>
    </View>
  );

  // ─── Action Sheet ─────────────────────────────────────────────────────────
  const renderActionSheet = () => {
    if (!selectedProject) return null;
    return (
      <Modal visible={showActionSheet} transparent animationType="slide" onRequestClose={() => setShowActionSheet(false)}>
        <TouchableOpacity style={s.overlay} activeOpacity={1} onPress={() => setShowActionSheet(false)} />
        <View style={s.actionSheet}>
          <View style={s.actionSheetHandle} />
          <Text style={s.actionSheetTitle} numberOfLines={1}>{selectedProject.name}</Text>
          <Text style={s.actionSheetSub}>{selectedProject.client}</Text>
          <View style={s.actionSheetStats}>
            <StatusBadge status={selectedProject.status} />
            <Text style={s.actionSheetProgress}>{selectedProject.progress}% complete</Text>
          </View>
          <View style={s.actionSheetGrid}>
            {[
              ...PROJECT_QUICK_ACTIONS,
              { key: 'planning', icon: 'chart-gantt', label: 'Planning', color: C.cyan, screen: 'ProjectPlanning' },
            ].map(action => (
              <TouchableOpacity key={action.key} style={s.actionSheetBtn} onPress={() => navigateTo(action.screen, selectedProject)}>
                <View style={[s.actionSheetBtnIcon, { backgroundColor: action.color + '20' }]}>
                  <MaterialCommunityIcons name={action.icon} size={22} color={action.color} />
                </View>
                <Text style={s.actionSheetBtnText}>{action.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={s.actionSheetDanger}>
            <TouchableOpacity style={s.dangerBtn} onPress={() => deleteProject(selectedProject)}>
              <MaterialCommunityIcons name="delete-outline" size={18} color={C.danger} />
              <Text style={[s.dangerBtnText]}>Delete Project</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.cancelBtn} onPress={() => setShowActionSheet(false)}>
              <Text style={s.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  };

  // ─── New Project Modal ────────────────────────────────────────────────────
  const renderNewModal = () => (
    <Modal visible={showNewModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowNewModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
        <View style={s.modalHeader}>
          <TouchableOpacity onPress={() => setShowNewModal(false)}>
            <MaterialCommunityIcons name="close" size={22} color={C.text} />
          </TouchableOpacity>
          <Text style={s.modalTitle}>New Project</Text>
          <TouchableOpacity onPress={createProject} disabled={creating}>
            <Text style={[s.modalSave, creating && { opacity: 0.5 }]}>{creating ? 'Creating...' : 'Create'}</Text>
          </TouchableOpacity>
        </View>
        <ScrollView style={s.modalBody} keyboardShouldPersistTaps="handled">
          {[
            ['Project Name *', 'name', 'e.g. CRM Implementation'],
            ['Description', 'description', 'Brief project overview...'],
            ['Client / Company', 'client', 'Client name'],
            ['Project Manager', 'projectManager', 'Manager name'],
            ['Category', 'category', 'e.g. Technology, Construction...'],
            ['Location', 'location', 'e.g. London, Remote'],
            ['Budget (£)', 'budget', '0'],
            ['Start Date', 'startDate', 'YYYY-MM-DD'],
            ['End Date', 'endDate', 'YYYY-MM-DD'],
          ].map(([label, field, ph]) => (
            <View key={field} style={s.formField}>
              <Text style={s.formLabel}>{label}</Text>
              <TextInput
                style={[s.formInput, field === 'description' && { height: 70, textAlignVertical: 'top' }]}
                placeholder={ph} placeholderTextColor={C.textSec}
                value={newProject[field]}
                onChangeText={v => setNewProject(p => ({ ...p, [field]: v }))}
                keyboardType={field === 'budget' ? 'decimal-pad' : 'default'}
                multiline={field === 'description'}
              />
            </View>
          ))}
          <View style={s.formField}>
            <Text style={s.formLabel}>Status</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {['Planning','In Progress','On Track','Delayed','On Hold','Completed'].map(st => (
                <TouchableOpacity key={st} style={[s.optionChip, newProject.status === st && s.optionChipActive]} onPress={() => setNewProject(p => ({ ...p, status: st }))}>
                  <Text style={[s.optionChipText, newProject.status === st && { color: C.primary }]}>{st}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
          <View style={s.formField}>
            <Text style={s.formLabel}>Priority</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {['Low', 'Medium', 'High'].map(pr => (
                <TouchableOpacity key={pr} style={[s.optionChip, newProject.priority === pr && s.optionChipActive]} onPress={() => setNewProject(p => ({ ...p, priority: pr }))}>
                  <Text style={[s.optionChipText, newProject.priority === pr && { color: C.primary }]}>{pr}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ─── Empty / Loading ─────────────────────────────────────────────────────
  if (loading) return (
    <SafeAreaView style={s.safe}>
      <LinearGradient colors={[C.primary, C.secondary]} style={s.header}>
        <View style={s.headerTop}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={s.backBtn}><Ionicons name="arrow-back" size={22} color="#fff" /></TouchableOpacity>
          <Text style={s.headerTitle}>Projects</Text>
          <View style={{ width: 40 }} />
        </View>
      </LinearGradient>
      <View style={s.loadingBox}><ActivityIndicator size={36} color={C.primary} /><Text style={s.loadingText}>Loading projects...</Text></View>
    </SafeAreaView>
  );

  // ─── Main Render ─────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="light-content" backgroundColor={C.primary} />
      <FlatList
        data={filtered}
        renderItem={renderProjectCard}
        keyExtractor={p => String(p.id)}
        numColumns={viewMode === 'grid' ? 2 : 1}
        key={viewMode}
        ListHeaderComponent={renderHeader}
        contentContainerStyle={[s.list, filtered.length === 0 && { flex: 1 }]}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadProjects(); }} colors={[C.primary]} />}
        ListEmptyComponent={
          <View style={s.emptyBox}>
            <MaterialCommunityIcons name="briefcase-search-outline" size={64} color={C.border} />
            <Text style={s.emptyTitle}>No projects found</Text>
            <Text style={s.emptySub}>{searchQuery || activeFilter !== 'All' ? 'Try changing your filters' : 'Create your first project to get started'}</Text>
            <TouchableOpacity style={s.emptyBtn} onPress={() => setShowNewModal(true)}>
              <MaterialCommunityIcons name="plus" size={18} color="#fff" />
              <Text style={s.emptyBtnText}>New Project</Text>
            </TouchableOpacity>
          </View>
        }
      />
      {renderActionSheet()}
      {renderNewModal()}
    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.bg },
  header: { paddingTop: 4, paddingBottom: 12 },
  headerTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 14, paddingVertical: 8 },
  backBtn: { padding: 4 },
  headerCenter: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  headerLogo: { width: 26, height: 26, borderRadius: 4 },
  headerTitle: { fontSize: 18, fontWeight: '900', color: '#fff' },
  headerActions: { flexDirection: 'row', gap: 4 },
  headerBtn: { padding: 6, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8 },
  statsScroll: { paddingHorizontal: 14, gap: 6, paddingBottom: 4 },
  statPill: { backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 6, alignItems: 'center', minWidth: 52 },
  statPillVal: { fontSize: 16, fontWeight: '900', color: '#fff' },
  statPillLabel: { fontSize: 9, color: 'rgba(255,255,255,0.65)', fontWeight: '600', marginTop: 1 },
  searchWrap: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, margin: 12, marginBottom: 6, borderRadius: 12, paddingHorizontal: 12, borderWidth: 1, borderColor: C.border },
  searchIcon: { marginRight: 6 },
  searchInput: { flex: 1, paddingVertical: 10, fontSize: 14, color: C.text },
  toolbar: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingBottom: 6, gap: 6 },
  toolbarBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 6, backgroundColor: C.card, borderRadius: 8, borderWidth: 1, borderColor: C.border },
  toolbarBtnText: { fontSize: 12, color: C.textSec, fontWeight: '600' },
  filterScroll: { flex: 1 },
  filterChip: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 16, borderWidth: 1, borderColor: C.border, backgroundColor: C.card },
  filterChipActive: { backgroundColor: C.primary, borderColor: C.primary },
  filterChipText: { fontSize: 11, color: C.textSec, fontWeight: '600' },
  filterChipTextActive: { color: '#fff' },
  viewToggle: { flexDirection: 'row', gap: 2 },
  viewBtn: { padding: 6, borderRadius: 8 },
  viewBtnActive: { backgroundColor: C.primary + '20' },
  extFilters: { paddingHorizontal: 12, paddingBottom: 10, backgroundColor: C.card, borderBottomWidth: 1, borderBottomColor: C.border },
  extFilterRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 6 },
  extFilterLabel: { fontSize: 11, fontWeight: '700', color: C.textSec, width: 50 },
  extChip: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, borderWidth: 1, borderColor: C.border, backgroundColor: '#F8FAFC' },
  extChipActive: { borderColor: C.primary, backgroundColor: C.primary + '15' },
  extChipText: { fontSize: 11, color: C.textSec, fontWeight: '600' },
  navShortcut: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 6, backgroundColor: C.primary + '10', borderRadius: 10, borderWidth: 1, borderColor: C.primary + '30' },
  navShortcutText: { fontSize: 11, color: C.primary, fontWeight: '700' },
  resultCount: { fontSize: 11, fontWeight: '700', color: C.textSec, paddingHorizontal: 14, paddingVertical: 4 },
  list: { paddingHorizontal: 12, paddingBottom: 24 },
  card: { marginBottom: 10 },
  cardInner: { backgroundColor: C.card, borderRadius: 16, overflow: 'hidden', shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 3 },
  cardHeader: { flexDirection: 'row', alignItems: 'flex-start', padding: 14, paddingBottom: 8 },
  cardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 2 },
  cardName: { flex: 1, fontSize: 14, fontWeight: '800', color: C.text },
  cardClient: { fontSize: 12, color: C.textSec },
  progressSection: { paddingHorizontal: 14, paddingBottom: 8 },
  progressLabelRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  progressLabel: { fontSize: 11, color: C.textSec, fontWeight: '500' },
  progressPct: { fontSize: 12, fontWeight: '800' },
  progressTrack: { height: 6, backgroundColor: C.border, borderRadius: 3, flex: 1 },
  progressFill: { height: 6, borderRadius: 3 },
  metricsRow: { flexDirection: 'row', paddingHorizontal: 14, paddingBottom: 8, gap: 12 },
  metric: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  metricText: { fontSize: 11, color: C.textSec, fontWeight: '500' },
  milestonesRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingBottom: 8, gap: 4 },
  milestoneDot: { width: 8, height: 8, borderRadius: 4 },
  milestonesMore: { fontSize: 9, color: C.textSec },
  milestonesLabel: { fontSize: 10, color: C.textSec, marginLeft: 2 },
  quickActions: { flexDirection: 'row', borderTopWidth: 1, borderTopColor: C.border },
  quickAction: { flex: 1, flexDirection: 'column', alignItems: 'center', paddingVertical: 8, gap: 2 },
  quickActionText: { fontSize: 9, fontWeight: '700' },
  badge: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 7, paddingVertical: 3, borderRadius: 8 },
  badgeText: { fontSize: 10, fontWeight: '700' },
  priorityDot: { width: 8, height: 8, borderRadius: 4 },
  gridCard: { flex: 1, margin: 4, backgroundColor: C.card, borderRadius: 14, overflow: 'hidden', shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 6, elevation: 2, maxWidth: (width - 32) / 2 },
  gridColorBar: { height: 4 },
  gridContent: { padding: 12 },
  gridName: { fontSize: 13, fontWeight: '800', color: C.text, marginBottom: 2 },
  gridClient: { fontSize: 11, color: C.textSec, marginBottom: 8 },
  gridProgress: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 },
  gridFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  gridDate: { fontSize: 9, color: C.textSec },
  loadingBox: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
  loadingText: { fontSize: 14, color: C.textSec },
  emptyBox: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 40 },
  emptyTitle: { fontSize: 18, fontWeight: '800', color: C.text, marginTop: 16 },
  emptySub: { fontSize: 13, color: C.textSec, textAlign: 'center', marginTop: 6, marginBottom: 24, lineHeight: 20 },
  emptyBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: C.primary, paddingHorizontal: 20, paddingVertical: 12, borderRadius: 12 },
  emptyBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)' },
  actionSheet: { backgroundColor: C.card, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 20, paddingBottom: 32 },
  actionSheetHandle: { width: 40, height: 4, backgroundColor: C.border, borderRadius: 2, alignSelf: 'center', marginBottom: 16 },
  actionSheetTitle: { fontSize: 17, fontWeight: '900', color: C.text, marginBottom: 2 },
  actionSheetSub: { fontSize: 13, color: C.textSec, marginBottom: 10 },
  actionSheetStats: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 16 },
  actionSheetProgress: { fontSize: 13, color: C.textSec },
  actionSheetGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 },
  actionSheetBtn: { alignItems: 'center', gap: 4, width: (width - 80) / 4 },
  actionSheetBtnIcon: { width: 48, height: 48, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  actionSheetBtnText: { fontSize: 10, color: C.text, fontWeight: '600', textAlign: 'center' },
  actionSheetDanger: { flexDirection: 'row', gap: 10 },
  dangerBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: '#FEE2E2', backgroundColor: '#FEF2F2' },
  dangerBtnText: { color: C.danger, fontWeight: '700', fontSize: 13 },
  cancelBtn: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 12, borderRadius: 12, backgroundColor: '#F1F5F9' },
  cancelBtnText: { color: C.textSec, fontWeight: '700', fontSize: 13 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: C.border },
  modalTitle: { fontSize: 16, fontWeight: '800', color: C.text },
  modalSave: { fontSize: 15, fontWeight: '700', color: C.primary },
  modalBody: { padding: 16 },
  formField: { marginBottom: 14 },
  formLabel: { fontSize: 12, fontWeight: '700', color: C.textSec, marginBottom: 5 },
  formInput: { backgroundColor: '#F8FAFC', borderRadius: 10, borderWidth: 1, borderColor: C.border, padding: 11, fontSize: 14, color: C.text },
  optionChip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 18, borderWidth: 1, borderColor: C.border, marginRight: 6 },
  optionChipActive: { borderColor: C.primary, backgroundColor: C.primary + '15' },
  optionChipText: { fontSize: 12, color: C.textSec, fontWeight: '600' },
});
